import { useEffect, useState } from "react";
import { Wand2, Send, Save, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { targetOptions, langDisplayName } from "@/lib/languages";
import { useDownload, TRANSFORMS, NOTE_MAX, libError } from "@/lib/libraryApi";
import { AssetAttach } from "@/components/assets/MissionAssets";

// Render → mandatory preview → assign (snapshot) / save / download. Never edits the canonical template.
const txt = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.map(txt).filter(Boolean).join("\n• ");
  if (typeof v === "object") return [v.title, v.instructions, v.prompt, v.description, v.task, txt(v.steps)].filter(Boolean).join(" — ");
  return String(v);
};
export default function RenderPanel({ templateId, memo = {} }) {
  const { t, locale } = useI18n();
  const download = useDownload();
  const saved = memo[templateId] || {};
  const [opt, setOpt] = useState(saved.opt || { target_language: new URLSearchParams(window.location.search).get("lang") || "fr", level_shift: 0, transform: null, note: "" });
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [renderErr, setRenderErr] = useState(null);
  const [draft, setDraft] = useState(saved.draft || null);
  useEffect(() => { memo[templateId] = { opt, draft }; }, [memo, templateId, opt, draft]);
  const [classes, setClasses] = useState([]);
  const [files, setFiles] = useState([]);
  const [assign, setAssign] = useState({ class_id: "", due_date: "", attachment_ids: [], asset_refs: [] });
  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data.active || [])).catch(() => {});
    api.get("/library/resources").then((r) => setFiles(r.data.resources || [])).catch(() => {});
  }, []);
  const set = (k, v) => setOpt((o) => ({ ...o, [k]: v }));

  const noteLen = opt.note.trim().length;
  const themeNeedsNote = opt.transform === "theme" && noteLen < 3;
  const valid = targetOptions().some((l) => l.code === opt.target_language) && noteLen <= NOTE_MAX && !themeNeedsNote;
  const render = async () => {
    if (!valid) return;
    setBusy(true); setDraft(null); setRenderErr(null);
    try { setDraft((await api.post(`/library/missions/${templateId}/render`, { ...opt, teacher_language: locale, note: opt.note.trim() || null })).data.mission); }
    catch (e) { setRenderErr({ msg: libError(e, t, t("lib.renderFailed")), retry: !e?.response || e.response.status >= 500 }); }
    finally { setBusy(false); }
  };
  // Assign/save create records, so no auto-retry: the teacher sees the reason and decides.
  const doAssign = async () => {
    if (!assign.class_id) return toast.error(t("lib.pickClass"));
    setSending(true);
    try {
      await api.post("/assignments", { mission: draft, class_ids: [assign.class_id], due_date: assign.due_date || null, attachment_ids: assign.attachment_ids, asset_refs: assign.asset_refs });
      toast.success(t("lib.assigned"));
    } catch (e) { toast.error(!e?.response ? t("lib.networkCheck") : libError(e, t)); }
    finally { setSending(false); }
  };
  const saveMine = async () => {
    setSending(true);
    try { await api.post("/missions", { mission: draft }); toast.success(t("lib.savedMine")); }
    catch (e) { toast.error(!e?.response ? t("lib.networkCheck") : libError(e, t)); }
    finally { setSending(false); }
  };
  const steps = (draft?.steps || []).filter((s) => s && typeof s === "object");

  return (
    <div className="space-y-3 mt-2" data-testid="lib-render-panel">
      <div className="grid sm:grid-cols-3 gap-2">
        <select className="nb-input" value={opt.target_language} onChange={(e) => set("target_language", e.target.value)} data-testid="lib-render-lang">
          {targetOptions().map((l) => <option key={l.code} value={l.code}>{langDisplayName(l.code, locale)}</option>)}
        </select>
        <select className="nb-input" value={opt.level_shift} onChange={(e) => set("level_shift", Number(e.target.value))} data-testid="lib-render-level">
          <option value={-1}>{t("lib.levelDown")}</option><option value={0}>{t("lib.levelSame")}</option><option value={1}>{t("lib.levelUp")}</option>
        </select>
        <div>
          <input className={`nb-input ${themeNeedsNote ? "border-coral" : ""}`} dir="auto" maxLength={NOTE_MAX} placeholder={opt.transform === "theme" ? t("lib.themePh") : t("lib.notePh")} value={opt.note} onChange={(e) => set("note", e.target.value)} aria-invalid={themeNeedsNote} data-testid="lib-render-note" />
          <div className={`text-[11px] mt-0.5 text-end ${noteLen > NOTE_MAX - 30 ? "text-coral" : "text-gray-500"}`} dir="ltr" data-testid="lib-note-count">{noteLen}/{NOTE_MAX}</div>
        </div>
      </div>
      {themeNeedsNote && <p className="text-xs text-coral" data-testid="lib-theme-note-required">{t("lib.themeNeedsNote")}</p>}
      <div className="flex flex-wrap gap-1.5" data-testid="lib-transforms">
        {TRANSFORMS.map((k) => (
          <button key={k} type="button" onClick={() => set("transform", opt.transform === k ? null : k)} className={`nb-tag text-xs ${opt.transform === k ? "bg-ink text-white" : "bg-white"}`} data-testid={`lib-tr-${k}`}>{t(`lib.tr.${k}`)}</button>
        ))}
      </div>
      <button onClick={render} disabled={busy || !valid} className="nb-btn-primary py-2 px-4 disabled:opacity-50" data-testid="lib-render-btn"><Wand2 className="w-4 h-4" /> {busy ? t("lib.rendering") : t("lib.render")}</button>
      {renderErr && (
        <div className="nb-card bg-peach p-3 text-sm flex items-center gap-3 flex-wrap" role="alert" data-testid="lib-render-error">
          <span>{renderErr.msg}</span>
          {renderErr.retry && <button onClick={render} className="underline font-bold" data-testid="lib-render-retry">{t("lib.retry")}</button>}
        </div>
      )}

      {draft && (
        <div className="nb-card bg-white p-4 space-y-2" data-testid="lib-preview">
          <div className="text-[11px] font-bold uppercase tracking-widest rtl:tracking-normal text-coral">{t("lib.previewNote")}</div>
          <h4 className="font-cabinet font-bold text-lg" dir="auto" data-testid="lib-preview-title">{draft.title}</h4>
          <p className="text-xs text-gray-600">{langDisplayName(draft.target_language, locale)} · {draft.level} · ~{draft.estimated_minutes} min</p>
          {draft.scenario && <p className="text-sm" dir="auto">{draft.scenario}</p>}
          <div className="space-y-2 text-sm" data-testid="lib-preview-steps">
            {["student_instructions", "warm_up", "core_task", "conversation_challenge", "extension"].map((k) => txt(draft[k]) && (
              <div key={k}><div className="text-[11px] font-bold uppercase tracking-widest rtl:tracking-normal text-gray-500">{steps.find((s) => s.type === k)?.title || k.replace(/_/g, " ")}</div><p dir="auto" className="whitespace-pre-line">{txt(draft[k])}</p></div>
            ))}
            {txt(draft.success_criteria) && <div><div className="text-[11px] font-bold uppercase tracking-widest rtl:tracking-normal text-gray-500">{t("lib.f.success_criteria")}</div><p dir="auto">{txt(draft.success_criteria)}</p></div>}
          </div>
          {(draft.target_vocabulary || []).length > 0 && (
            <div className="flex flex-wrap gap-1">{draft.target_vocabulary.map((v, i) => <span key={i} className="nb-tag bg-lavender text-xs normal-case tracking-normal"><span dir="auto">{v.term}</span> · <bdi>{v.translation}</bdi></span>)}</div>
          )}
          <div className="grid sm:grid-cols-3 gap-2 pt-2">
            <select className="nb-input" value={assign.class_id} onChange={(e) => setAssign({ ...assign, class_id: e.target.value })} data-testid="lib-assign-class">
              <option value="">{t("lib.pickClass")}</option>
              {classes.map((c) => <option key={c.class_id} value={c.class_id}>{c.name}</option>)}
            </select>
            <input type="date" className="nb-input" value={assign.due_date} onChange={(e) => setAssign({ ...assign, due_date: e.target.value })} data-testid="lib-assign-due" />
            <fieldset className="nb-input h-auto max-h-28 overflow-y-auto py-1" data-testid="lib-assign-files">
              <legend className="text-[11px] font-bold text-gray-500 px-1">{t("lib.attachFiles")}</legend>
              {files.length === 0 && <p className="text-xs text-gray-500">{t("lib.filesEmpty")}</p>}
              {files.map((f) => (
                <label key={f.resource_id} className="flex items-center gap-2 text-xs py-0.5">
                  <input type="checkbox" checked={assign.attachment_ids.includes(f.resource_id)} data-testid={`lib-attach-${f.resource_id}`}
                    onChange={(e) => setAssign({ ...assign, attachment_ids: e.target.checked ? [...assign.attachment_ids, f.resource_id] : assign.attachment_ids.filter((x) => x !== f.resource_id) })} />
                  <span className="truncate" dir="auto">{f.filename}</span>
                </label>
              ))}
            </fieldset>
            <AssetAttach key={draft.target_language} language={draft.target_language} value={assign.asset_refs} onChange={(v) => setAssign((x) => ({ ...x, asset_refs: v }))} />
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={doAssign} disabled={sending} className="nb-btn-primary py-2 px-4 text-sm" data-testid="lib-assign-btn"><Send className="w-4 h-4" /> {t("lib.assign")}</button>
            <button onClick={saveMine} disabled={sending} className="nb-btn-outline py-2 px-3 text-sm" data-testid="lib-save-mine"><Save className="w-4 h-4" /> {t("lib.saveMine")}</button>
            <button onClick={() => download("/library/export", "mission.pdf", { mission: draft, format: "pdf" })} className="nb-btn-outline py-2 px-3 text-sm" data-testid="lib-draft-pdf"><Download className="w-4 h-4" /> PDF</button>
            <button onClick={() => download("/library/export", "mission.docx", { mission: draft, format: "docx" })} className="nb-btn-outline py-2 px-3 text-sm" data-testid="lib-draft-docx"><Download className="w-4 h-4" /> DOCX</button>
          </div>
        </div>
      )}
    </div>
  );
}
